export const performanceHome = {
  title: 'SEO details · {{name}}',
  subtitle: 'Impact on positioning, content and traffic for the selected period.',
  partialFilterBanner:
    'Live filters applied partially: Clicks, Impressions, CTR and Branded vs Non-branded reflect the selected filters. Traffic evolution, Top keywords and Countries still show site-wide totals (snapshot).',
  filters: {
    comparison: {
      label: 'Compare vs',
      previousPeriod: 'Previous period',
      previousYear: 'Previous year',
      custom: 'Custom',
      goal: 'Objective',
      none: 'No comparison',
    },
    scope: {
      label: 'Scope',
      all: 'All pages',
      blog: 'Blog only',
    },
    scopes: {
      label: 'Scope',
      placeholder: 'Select scopes',
      summary: '{{count}} selected',
      summaryAll: 'All',
      'all': 'All pages',
      'platform-blog': 'Platform blogs',
      'legacy-blog': 'Legacy blog',
    },
    brand: {
      label: 'Brand',
      all: 'Brand + Non-brand',
      branded: 'Brand only',
      'non-branded': 'Non-brand only',
    },
    country: {
      label: 'Country',
      all: 'All countries',
      searchPlaceholder: 'Search country…',
      empty: 'No results',
      clear: 'Clear country filter',
      multiSelected: '{{count}} countries',
    },
    device: {
      label: 'Device',
      all: 'All',
      mobile: 'Mobile',
      desktop: 'Desktop',
      tablet: 'Tablet',
    },
  },
  textFilter: {
    trigger: 'Advanced filters',
    heading: 'Advanced filters',
    dimension: {
      query: 'Query',
      url: 'URL',
    },
    activeChipsLabel: 'Active filters',
    oneOf: {
      placeholder: 'One value per line — paste your URL list here',
      count: '{{count}} values',
      chipValue: '{{count}} values',
      limitExceeded: 'Too many values: maximum {{maxValues}} entries / {{maxChars}} characters',
    },
  },
  segments: {
    trigger: 'My panels',
    heading: 'Saved panels',
    savedHeading: 'Your panels',
    saveAs: 'Save current view…',
    updateActive: "Update '{{name}}'",
    dirtyHint: 'Modified — current filters differ from the saved panel',
    share: 'Share with organization',
    sharedBadge: 'Shared',
    remove: 'Remove panel',
    saveDialog: {
      title: 'Save panel',
      description: 'Capture the current filters as a reusable panel.',
      placeholder: 'Panel name',
      submit: 'Save',
    },
    errors: {
      create: 'Could not save the panel. Please try again.',
      update: 'Could not update the panel. Please try again.',
      remove: 'Could not remove the panel. Please try again.',
      share: 'Could not share the panel. Please try again.',
    },
    invalidRegexDropped: 'Conditions with invalid regex were dropped from the segment.',
  },
  goals: {
    trigger: 'Objectives',
    title: 'Dashboard objectives',
    description: 'Configure site-wide goals for {{from}} to {{to}}.',
    loading: 'Loading current objectives...',
    empty: 'No objectives configured for this period yet.',
    retry: 'Try again',
    save: 'Save objectives',
    saved: 'Objectives saved.',
    deleted: 'Objective deleted.',
    cancel: 'Cancel',
    placeholder: 'Positive integer',
    currentValue: 'Current applied goal: {{value}}',
    currentValueLabel: 'Current applied objective',
    appliedPeriod: 'This objective applies to {{from}} - {{to}}.',
    sourcePeriods: 'Applied source periods',
    deleteMetric: 'Delete {{metric}} objective',
    deleteShort: 'Delete',
    deleteApplied: 'Delete this objective',
    overlapHint:
      'Overlapping objectives are applied. Delete the source periods before saving a new objective for this range.',
    status: {
      exact: 'Exact objective configured',
      overlap: 'Overlapping objective applied',
      empty: 'No objective for this period',
    },
    periodMode: {
      label: 'Objective period',
      total: 'Total selected range',
      month: 'Monthly',
      quarter: 'Quarterly',
    },
    fields: {
      clicks: 'Organic clicks',
      impressions: 'Organic impressions',
    },
    errors: {
      load: 'Could not load objectives. Please try again.',
      invalidValue: 'Objectives must be positive integers.',
      emptySave: 'Enter at least one objective before saving.',
      overlap: 'Delete overlapping objectives for this metric before saving.',
      delete: 'Could not delete the objective. Please try again.',
    },
  },
  coverage: {
    trigger: 'See which blocks each filter affects',
    heading: 'Which filters apply to each block?',
    description:
      'Not every block reads the same dataset. This table shows which filters actually narrow each card on the current view.',
    legend: {
      full: 'Applies',
      partial: 'Partial',
      none: 'Ignored',
      na: 'N/A',
    },
    columns: {
      block: 'Block',
      date: 'Date',
      scope: 'Scope',
      brand: 'Brand',
      country: 'Country',
      device: 'Device',
      text: 'Query / URL',
      drilldownUrl: '↓ URL',
      drilldownQuery: '↓ Query',
      note: 'Reason',
    },
    blocks: {
      heroKpis: 'Hero KPIs (clicks, impr, CTR, position)',
      brandedSplit: 'Branded vs Non-Branded',
      trafficTrend: 'Traffic trend',
      topCountries: 'Top countries',
      topPages: 'Top pages',
      topKeywords: 'Top keywords',
      conversions: 'Conversions (GA4)',
      domainAuthority: 'Domain Authority',
      pagesToOptimize: 'Pages to Optimize',
      aiSearch: 'AI Search (GA4)',
      seoKpis: 'SEO KPIs',
      outcomes: 'Outcomes',
      topKeywordsCompact: 'Top keywords (compact)',
      intention: 'Search intent',
      aiKpis: 'AI KPIs',
      aiSessionsTrend: 'AI sessions per day',
      aiTopPages: 'AI top pages',
    },
    notes: {
      scopePartial:
        'When Scope ≠ "All pages", hero and branded split recompute; trend and top countries stay site-wide (snapshot path).',
      brandedDefinesSplit: 'Brand defines the split of this card, it does not filter it.',
      snapshotSiteWide:
        'On the snapshot path, trend and top countries stay site-wide; on the live path (country/device/text active) they get filtered.',
      countryCollapses: 'Filtering by country collapses Top Countries to a single row.',
      topPagesHiddenOnUrlDrilldown: 'Table is hidden during URL drill-down.',
      topKeywordsHiddenOnQueryDrilldown: 'Table is hidden during query drill-down.',
      ga4Source: 'Data comes from GA4 which does not accept GSC filters.',
      ga4ScopeOnly: 'GA4 only respects the date range and its own scope.',
      aiTopPagesDrilldownWritesUrl:
        'The click writes ?pageUrl= to the URL but the AI section does not filter. Useful if you then open SEO Detail with the exact URL.',
      weeklySnapshot: 'Weekly DataForSEO snapshot, no period filter.',
      pagesToOptimizeBlogPath: 'Only respects the blogPathPrefix configured on the integration.',
    },
  },
  urlDrilldown: {
    back: 'Back to list',
    label: 'Filtering by:',
    clear: 'Clear URL filter',
    siteWideTooltip: 'Site-wide metric, not affected by filters',
    aiTrafficBadge: 'Filtered by URL',
  },
  queryDrilldown: {
    label: 'Filtering by query:',
    back: 'Back to list',
    clear: 'Clear query filter',
  },
  hero: {
    organicClicks: 'Organic clicks',
    impressions: 'Impressions',
    ctr: 'CTR',
    avgPosition: 'Average position',
    aiUsers: 'Users from AI',
    goalProgress: '{{percent}}% of {{goal}}',
    momDelta: '{{percent}} vs previous period',
    momDeltaAbsolute: '{{value}} vs previous period',
    noComparison: 'No comparison',
    noGoal: 'No objective configured',
  },
  seo: {
    sectionTitle: 'SEO · Search Console',
    trafficTrend: {
      title: 'Traffic evolution',
      subtitle: 'Clicks and impressions across the selected period. Excludes the in-progress bucket.',
      clicks: 'Clicks',
      impressions: 'Impressions',
      preliminary: 'Preliminary',
      preliminaryBucket: 'Includes preliminary days',
      dataThrough: 'Data through {{date}}.',
      preliminaryNote:
        'Dashed segments are preliminary: Search Console is still processing them and they may change.',
      noDataYet:
        'No data for this period yet: Search Console publishes each day on the following day.',
    },
    brandedSplit: {
      title: 'Branded vs Non-branded',
      subtitle: 'Share of organic clicks coming from non-branded queries.',
      branded: 'Branded',
      nonBranded: 'Non-branded',
      nonBrandedShare: 'Non-branded share',
      goal: 'Non-branded goal: {{value}}',
    },
    topPages: {
      title: 'Page performance',
      subtitle: 'Highest performing pages for the selected period.',
      subtitlePlatformBlogs:
        'Deliverable notes and Referent publications with data, with their performance.',
      platformBlogSummary: {
        title: 'Deliverable notes',
        total: '{{value}} notes logged in Deliverables',
        empty: 'No notes logged in Deliverables.',
        withData: 'With data',
        withoutImpressions: 'No impressions',
        withoutUrl: 'No URL',
        outsideProperty: 'Outside the property',
        publicationsNotInDeliverables: '{{value}} published from Referent and not in Deliverables',
        hint: 'To fix a note, edit it in Deliverables.',
        error: 'We could not load the notes summary.',
      },
      blogBadge: 'Blog',
      searchPlaceholder: 'Search by URL…',
      pageSizeLabel: 'Rows per page',
      empty: 'No pages match the current filters.',
      syncing: 'Building full-page snapshot — refresh in a moment.',
      columns: {
        url: 'URL',
        clicks: 'Clicks',
        impressions: 'Impressions',
        ctr: 'CTR',
        avgPosition: 'Avg. position',
      },
    },
    topKeywords: {
      title: 'Top keywords',
      subtitle: 'Queries driving the most impact.',
      brandedBadge: 'Brand',
      nonBrandedBadge: 'Non-brand',
      searchPlaceholder: 'Search by query…',
      pageSizeLabel: 'Rows per page',
      empty: 'No keywords match the current filters.',
      syncing: 'Building full-keyword snapshot — refresh in a moment.',
      brandFilter: {
        all: 'All',
        branded: 'Brand only',
        nonBranded: 'Non-brand only',
      },
      columns: {
        query: 'Query',
        clicks: 'Clicks',
        impressions: 'Impressions',
        ctr: 'CTR',
        avgPosition: 'Avg. position',
      },
    },
    pagination: {
      summary: 'Showing {{from}}-{{to}} of {{total}}',
      prev: 'Previous',
      next: 'Next',
    },
  },
  effortVsGrowth: {
    title: 'Effort vs growth',
    betaBadge: 'Beta',
    subtitle:
      'Monthly content published from the platform vs the organic clicks the site receives.',
    summaryBlogs: '{{count}} blogs published',
    summaryEmpty: 'No platform-published blogs in this period.',
    chartClicksLabel: 'Organic clicks',
    chartBlogsLabel: 'Blogs published',
    tooltipBlogsTitle: 'Blogs published this month',
    feedbackPrompt: 'Is this chart useful?',
    feedbackThanks: 'Thanks for the feedback.',
    feedbackYes: 'Yes',
    feedbackNo: 'No',
  },
  conversions: {
    title: 'Sign-ups',
    subtitle: 'Conversions from organic + AI traffic. Excludes direct.',
    notConfigured: 'GA4 not connected',
    notConfiguredHint: 'Connect GA4 to start tracking conversions.',
    loading: 'Crunching conversions…',
    loadingHint: 'Querying GA4 for organic + AI signups.',
    deltaUp: '+{{value}} vs previous',
    deltaDown: '{{value}} vs previous',
    deltaFlat: 'No change',
    breakdown: {
      organic: 'Organic',
      ai: 'From AI',
    },
  },
  domainAuthority: {
    title: 'Domain authority',
    score: 'Authority score',
    referringDomains: 'Referring domains',
    backlinks: 'Backlinks',
    deltaUp: '+{{value}} vs previous',
    deltaDown: '{{value}} vs previous',
    deltaFlat: 'No change',
    noDelta: 'First snapshot',
    notAvailable: 'Data not available yet',
    notAvailableHint: 'We pull this weekly. Check back in a few hours.',
    tooltip:
      'Authority score (0-100) estimates the strength of the domain based on its backlink profile. Updated weekly via DataForSEO.',
  },
  blogDetail: {
    selectorLabel: 'Inspect a specific blog',
    selectorPlaceholder: 'Pick a published blog…',
    searchPlaceholder: 'Search by title or URL…',
    empty: 'No published blogs found.',
    clear: 'Clear selection',
    daysSincePublished: '{{days}} days ago',
    topQueriesTitle: 'Top queries for this page',
    noQueries: 'No GSC queries for this page in the selected period.',
    error: 'Could not load blog detail.',
    scopeLockedBadge: '1 blog selected',
  },
  ai: {
    sectionTitle: 'Traffic from AI',
    mentionShare: {
      title: 'AI mention share',
      subtitle: 'How LLMs cite {{brand}} vs the competitive set.',
      aiUsers: 'users from AI',
      topReferrer: 'Top referrer: {{platform}}',
    },
    authority: {
      title: 'External authority',
      subtitle: 'Profiles on sources that feed LLM entity recognition.',
      completeRatio: '{{complete}} of {{total}} complete',
      status: {
        complete: 'Complete',
        incomplete: 'Incomplete',
        missing: 'Missing',
      },
    },
  },
  content: {
    sectionTitle: 'Monthly deliverables',
    subtitle: 'Output produced against the monthly target.',
    metricProgress: '{{percent}}% of goal',
    metrics: {
      backlinks: 'Backlinks',
      notasGeneradas: 'Articles drafted',
      notasPublicadas: 'Articles published',
      landingsGeneradas: 'Landings drafted',
      landingsPublicadas: 'Landings published',
      optimizaciones: 'Optimizations',
    },
  },
  pagesToOptimize: {
    sectionTitle: 'Pages to optimize',
    title: 'Opportunities detected',
    subtitle:
      'Top 10 with low CTR or page-2 with high CTR — highest-uplift candidates.',
    loading: 'Looking for candidates in Search Console…',
    error: "Couldn't load candidates. Retry later.",
    empty:
      'No candidate pages detected in this range. Try widening the date range.',
    columns: {
      url: 'URL',
      clicks: 'Clicks',
      impressions: 'Impressions',
      ctr: 'CTR',
      position: 'Position',
      tier: 'Tier',
    },
    tiers: {
      tier1_top10_low_ctr: 'Top 10 · low CTR',
      tier2_page2_high_ctr: 'Page 2 · high CTR',
    },
  },
  bulkOptimization: {
    submit: 'Optimize selected ({{count}})',
    selectionCount: '{{count}} of {{max}} selected',
    selectRow: 'Select {{path}}',
    selectAll: 'Select all',
    retry: 'Retry',
    useSkill: 'Use skill',
    copied: 'Skill command copied to clipboard',
    copyFailed: "Couldn't copy the command",
    columns: {
      status: 'Status',
    },
    status: {
      queued: 'Queued',
      running: 'Running',
      pending_cms_push: 'Publishing',
      applied: 'Applied',
      needs_review: 'Needs review',
      rolled_back: 'Rolled back',
      failed: 'Failed',
    },
    cms: {
      webflow: 'Webflow',
      wordpress: 'WordPress',
    },
    disabled: {
      no_cms: 'Connect a Webflow or WordPress integration first.',
      framer_only:
        "Framer in-place updates aren't supported yet — use the Framer editor.",
      wordpress_com: 'WordPress.com is not supported yet — use self-hosted WordPress.',
      expired: 'Reconnect your CMS to keep optimizing.',
      job_in_flight: 'A batch is already running — wait for it to finish.',
      empty_selection: 'Select at least one page to optimize.',
    },
    toast: {
      submitted: 'Optimizing {{count}} pages — you can leave this page.',
      applied: 'Optimized {{path}}',
      needsReview: '{{path}} needs review',
      failed: 'Failed to optimize {{path}}',
    },
    submitError: {
      JOB_ALREADY_IN_FLIGHT: 'A batch is already running for your organization.',
      RUN_ALREADY_IN_FLIGHT: 'Some selected URLs are already being optimized.',
      INTEGRATION_NOT_CONNECTED: 'Connect a Webflow or WordPress integration first.',
      INTEGRATION_PROVIDER_NOT_SUPPORTED:
        "This CMS isn't supported for bulk optimization yet.",
      INTEGRATION_NOT_HEALTHY: 'Reconnect your CMS to keep optimizing.',
      ROW_LIMIT_EXCEEDED: 'You can optimize at most 10 pages at once.',
      EMPTY_SELECTION: 'Select at least one page to optimize.',
      DUPLICATE_URLS_IN_REQUEST: 'Your selection contains duplicate URLs.',
      QUEUE_UNAVAILABLE: 'The queue is temporarily unavailable — try again shortly.',
      RETRY_LIMIT_EXCEEDED: 'This page has reached the retry limit.',
    },
    modal: {
      title: 'Optimize {{count}} pages?',
      description:
        'Each page runs the full optimization pipeline and is published to your CMS automatically.',
      targetCms: 'Target CMS',
      site: 'Site',
      estimatedCost: 'Estimated cost',
      estimateNote: 'Estimate only — the real cost is reported after each page runs.',
      confirm: 'Optimize {{count}}',
    },
    drawer: {
      title: 'Before / after',
      keyword: 'Target keyword',
      needsReviewReason: 'Needs manual review ({{reason}}) — not published to the CMS.',
      h1: 'H1',
      titleTag: 'Title tag',
      metaDescription: 'Meta description',
      wordCount: 'Word count',
      kwMentions: 'Keyword mentions',
      noDiff: 'No field changes recorded for this run.',
      cost: 'AI cost: ${{cost}}',
    },
    banner: {
      summary: '{{applied}} applied · {{failed}} failed · {{needsReview}} need review',
      cost: 'cost ${{cost}}',
      dismiss: 'Dismiss',
    },
  },
  aiTrafficPage: {
    title: 'Traffic from AI',
    subtitle:
      'GA4 metrics for visitors arriving from AI platforms — who comes, how they engage, and where authority is built.',
    loading: {
      title: 'Connecting to GA4',
      subtitle: 'Syncing sessions and events from AI platforms…',
    },
  },
  aiTraffic: {
    title: 'Google Analytics 4',
    subtitle: 'Site traffic segmented by source type.',
    scopeToggle: {
      label: 'Filter by traffic type',
      ai: 'AI only',
      all: 'All traffic',
    },
    cards: {
      totalUsers: 'Users',
      sessions: 'Sessions',
      engagementRate: 'Engagement Rate',
      bounceRate: 'Bounce Rate',
      eventCount: 'Events',
    },
    sessionsChart: {
      title: 'Sessions over time',
      current: 'Current period',
      previous: 'Previous period',
    },
    notConnected: {
      title: 'Google Analytics 4 is not connected',
      description: 'Connect GA4 to see traffic segmented by source.',
      cta: 'Connect GA4',
    },
    kpis: {
      aiUsers: 'AI users',
      aiSessions: 'AI sessions',
      engagementRate: 'Engagement rate',
      bounceRate: 'Bounce rate',
      avgEngagement: 'Avg. engagement time',
      keyEvents: 'Key events',
    },
    sessionsTrend: {
      title: 'AI sessions over time',
      subtitle: 'Solid: current period · dashed: previous period.',
      current: 'Current',
      previous: 'Previous',
      empty: 'No daily data for this period. Try refreshing or widening the range.',
    },
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    topPages: {
      title: 'Top pages by AI traffic',
      subtitle: 'Where visitors from LLMs land and how they engage.',
      columns: {
        page: 'Page',
        sessions: 'Sessions',
        totalUsers: 'Total users',
        engagement: 'User engagement',
        views: 'Views',
        keyEvents: 'Key events',
      },
    },
    platformEngagement: {
      title: 'Engagement by AI platform',
      subtitle: 'Sessions, avg time on site and bounce rate per LLM referrer.',
      avgTime: 'Avg time: {{value}}',
      bounceRate: 'Bounce: {{value}}',
    },
  },
  deliverablesPage: {
    title: 'Monthly deliverables',
    subtitle: 'Output produced this period vs the monthly target.',
  },
  deliverables: {
    pageKicker: 'Scorecard',
    kicker: 'April 2026',
    ringLabel: 'Overall completion',
    featured: 'Highlight',
    progressOf: '{{percent}}% of goal',
    headline: {
      onPace:
        '{{delivered}} of {{total}} deliverables shipped — on pace to close the month strong.',
      steady: '{{delivered}} of {{total}} deliverables done — steady progress with room to push.',
      behind:
        '{{delivered}} of {{total}} deliverables done — trailing the plan, focus the next weeks.',
    },
    subhead:
      '{{onTrack}} of {{total}} streams are on track this month, {{behind}} need attention. Push on publication pipelines to convert drafts into live output.',
    stats: {
      delivered: 'Delivered',
      onTrack: 'On track',
      behind: 'Behind',
    },
    status: {
      complete: 'Complete',
      over: 'Over-delivered',
      on: 'On track',
      atRisk: 'At risk',
      behind: 'Behind',
    },
    breakdown: {
      kicker: 'By stream',
      title: 'Breakdown by deliverable',
    },
    velocity: {
      kicker: 'Pacing',
      title: 'Weekly velocity',
      subtitle: 'Cumulative deliverables this month against the monthly pacing target.',
      ofTargetPace: 'of target pace',
      pace: 'Pacing target',
      actual: 'Delivered',
      monthTarget: 'Monthly goal',
    },
    alert: {
      label: 'Needs attention',
      copy: 'Only {{actual}} of {{goal}} published. Drafts are ready — unblock publishing to recover the month.',
      cta: 'Open queue',
    },
  },
  gsc: {
    emptyState: {
      eyebrow: 'Search Console',
      title: 'Connect Google Search Console to see your performance',
      description:
        'We pull clicks, impressions, CTR, average position, top pages and queries from GSC. Without it, this dashboard stays empty.',
      features: {
        realTraffic: 'Real clicks and impressions data',
        trendAnalysis: '13 months of monthly trend analysis',
        topContent: 'Top pages and queries — branded vs non-branded',
      },
      cta: 'Connect Google Search Console',
    },
    syncing: {
      title: 'Syncing your Search Console data',
      description:
        'You just connected GSC. The first load can take a few minutes — refresh shortly to see real metrics.',
    },
    error: {
      title: 'We could not load Search Console data',
      description: 'Try refreshing the page. If the error persists, check the integration.',
      cta: 'Review integration',
    },
    blogPrefix: {
      label: 'Blog path prefix',
      description: 'Used to filter "Blog only" metrics. For example: /blog or /resources.',
      placeholder: '/blog',
      save: 'Save',
      saved: 'Saved',
      invalid: 'Must start with "/" and contain only letters, numbers, hyphens, or slashes',
    },
    lastSyncedAt: 'Last synced: {{relative}}',
    preview: {
      organicClicks: 'Organic clicks',
      impressions: 'Impressions',
      ctr: 'CTR',
      badge: 'Preview',
    },
  },
  seoSummary: {
    title: 'Executive view · {{name}}',
    subtitle:
      'Consolidated SEO and AI traffic — designed for a quick read of the domain health.',
    blocks: {
      aiSearch: {
        title: 'AI Search',
        subtitle: 'Visibility and traffic from AI platforms (GA4).',
        platforms: {
          title: 'LLM distribution',
          totalSessions: '{{value}} sessions',
          empty: 'No AI platform sessions yet in this range.',
        },
      },
      seo: {
        title: 'SEO',
        subtitle: 'Organic performance from Google Search Console.',
      },
      outcomes: {
        title: 'Outcomes',
        subtitle: 'Domain authority and conversions for the period.',
      },
    },
    countries: {
      title: 'Country distribution',
      subtitle: 'Where your organic traffic comes from.',
      empty: 'No per-country data yet for this range.',
    },
    intention: {
      title: 'Search intent',
      subtitle: 'How your queries split across the different intent types.',
      empty: 'Not enough queries to classify yet.',
      heuristicNote:
        'Heuristic classification over your top queries — useful as a directional read.',
      types: {
        informational: 'Informational',
        commercial: 'Commercial',
        transactional: 'Transactional',
        navigational: 'Navigational',
      },
    },
    viewDetails: 'See SEO details',
    viewAiTraffic: 'See AI traffic',
  },
  ghost: {
    sectionTitle: 'Coming soon',
    comingSoon: 'Coming soon',
    organicVsPaid: {
      title: 'Organic vs Paid',
      description: 'Lead source split — connect GA4 to unlock.',
    },
    aiReadiness: {
      title: 'AI readiness',
      description: 'llms.txt, schema coverage, passage citability.',
    },
    contentReuse: {
      title: 'Content reuse',
      description: 'Top organic articles not yet reused on other channels.',
    },
    omnichannel: {
      title: 'Omnichannel calendar',
      description: 'Upcoming posts across blog, LinkedIn, YouTube.',
    },
  },
} as const
